/**
 * PLP Toolbar — collection title, results count, sort by, items per page, view toggle.
 * Matches Figma: title+count left | sort + items-per-page + view-toggle right.
 */
(function () {
  const { html, useState, useEffect, useRef } = window.preact;

  function GridIcon() {
    return html`
      <svg viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="22" height="22" rx="2.5" stroke="currentColor"/>
        <rect x="4.81" y="4.81" width="5.88" height="5.88" rx="0.53" fill="currentColor"/>
        <rect x="12.41" y="4.81" width="5.88" height="5.88" rx="0.53" fill="currentColor"/>
        <rect x="4.81" y="12.41" width="5.88" height="5.88" rx="0.53" fill="currentColor"/>
        <rect x="12.41" y="12.41" width="5.88" height="5.88" rx="0.53" fill="currentColor"/>
      </svg>
    `;
  }

  function ListIcon() {
    return html`
      <svg viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="22" height="22" rx="2.5" stroke="currentColor"/>
        <rect x="4.81" y="4.81" width="2.14" height="2.14" fill="currentColor"/>
        <rect x="7.49" y="4.81" width="10.7" height="2.14" fill="currentColor"/>
        <rect x="4.81" y="8.56" width="2.14" height="2.14" fill="currentColor"/>
        <rect x="7.49" y="8.56" width="10.7" height="2.14" fill="currentColor"/>
        <rect x="4.81" y="12.3" width="2.14" height="2.14" fill="currentColor"/>
        <rect x="7.49" y="12.3" width="10.7" height="2.14" fill="currentColor"/>
        <rect x="4.81" y="16.05" width="2.14" height="2.14" fill="currentColor"/>
        <rect x="7.49" y="16.05" width="10.7" height="2.14" fill="currentColor"/>
      </svg>
    `;
  }

  var isMobile = window.innerWidth <= 749;

  var SORT_OPTIONS = [
    { value: 'relevance', label: 'Best Match' },
    { value: 'newest',    label: 'Newest' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
  ];

  function CustomSortDropdown({ sortBy, onSortChange }) {
    var [open, setOpen] = useState(false);
    var ref = useRef(null);
    var current = SORT_OPTIONS.find(function(o) { return o.value === sortBy; }) || SORT_OPTIONS[0];

    useEffect(function() {
      if (!open) return;
      function handleOutside(e) {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      }
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside);
      return function() {
        document.removeEventListener('mousedown', handleOutside);
        document.removeEventListener('touchstart', handleOutside);
      };
    }, [open]);

    function selectOption(val) {
      onSortChange(val);
      setOpen(false);
    }

    return html`
      <div class="plp-sort-custom" ref=${ref}>
        <button type="button" class="plp-sort-custom__trigger" onClick=${function() { setOpen(!open); }}>
          <span class="plp-sort-custom__label">Sort By:</span>
          <span class="plp-sort-custom__value">${current.label}</span>
          <svg class="plp-sort-custom__arrow${open ? ' plp-sort-custom__arrow--open' : ''}" width="10" height="6" viewBox="0 0 10 6" fill="none"><path d="M1 1l4 4 4-4" stroke="#F04F36" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        ${open && html`
          <ul class="plp-sort-custom__menu">
            ${SORT_OPTIONS.map(function(opt) {
              return html`
                <li key=${opt.value}>
                  <button
                    type="button"
                    class="plp-sort-custom__option${opt.value === sortBy ? ' plp-sort-custom__option--active' : ''}"
                    onClick=${function() { selectOption(opt.value); }}
                    onTouchEnd=${function(e) { e.preventDefault(); selectOption(opt.value); }}
                  >${opt.label}</button>
                </li>
              `;
            })}
          </ul>
        `}
      </div>
    `;
  }

  function FilterIcon() {
    return html`
      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 3h13M3.5 8h9M5.5 13h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
      </svg>
    `;
  }

  function Toolbar({
    totalResults, viewMode, sortBy, hitsPerPage,
    pageType, searchQuery, collectionTitle,
    onToggleView, onSortChange, onHitsPerPageChange, onOpenFilters
  }) {
    var title = '';
    if (pageType === 'search' && searchQuery) {
      title = 'Search: "' + searchQuery + '"';
    } else if (collectionTitle) {
      title = collectionTitle;
    }

    return html`
      <div class="plp-toolbar">
        <div class="plp-toolbar__left">
          <button class="plp-toolbar__filter-btn" onClick=${onOpenFilters}>
            <${FilterIcon} />
            <span>Filter</span>
          </button>
          <div class="plp-toolbar__title-group" ref=${function(el) {
            if (!el) return;
            var bc = document.getElementById('algolia-breadcrumbs');
            if (bc && bc.parentNode !== el) {
              // Breadcrumbs go ON TOP of the title, so the widget is inserted
              // first, not appended after the count. Display is left to the
              // stylesheets (an inline flex would outrank the desktop-collection
              // hide rule in plp-breadcrumbs.css).
              bc.style.removeProperty('display');
              el.insertBefore(bc, el.firstChild);
            }
          }}>
            ${pageType !== 'search' && title && html`
              <nav class="breadcrumbs plp-toolbar__breadcrumbs" aria-label="breadcrumbs">
                <ol class="breadcrumbs__list">
                  <li class="breadcrumbs__item"><a class="breadcrumbs__link" href="/">Home</a></li>
                  ${(window.algoliaCollectionContext && window.algoliaCollectionContext.parents || []).map(function (p) {
                    return p && p.title && html`
                      <li class="breadcrumbs__separator" aria-hidden="true">/</li>
                      <li class="breadcrumbs__item">
                        ${p.url && p.url !== '#'
                          ? html`<a class="breadcrumbs__link" href="${p.url}">${p.title}</a>`
                          : html`<span class="breadcrumbs__link">${p.title}</span>`}
                      </li>
                    `;
                  })}
                  <li class="breadcrumbs__separator" aria-hidden="true">/</li>
                  <li class="breadcrumbs__item"><span class="breadcrumbs__link" aria-current="page">${title}</span></li>
                </ol>
              </nav>
            `}
            ${pageType !== 'search' && title && html`<h1 class="plp-toolbar__title">${title}</h1>`}
            ${pageType !== 'search' && totalResults != null && html`<span class="plp-toolbar__count">${totalResults} Results</span>`}
          </div>
        </div>
        <div class="plp-toolbar__right">
          ${isMobile ? html`
            <${CustomSortDropdown} sortBy=${sortBy} onSortChange=${onSortChange} />
          ` : html`
            <div class="plp-toolbar__sort">
              <label class="plp-toolbar__sort-label" for="plp-sort">Sort By:</label>
              <select
                id="plp-sort"
                class="plp-toolbar__sort-select"
                value=${sortBy}
                onChange=${(e) => onSortChange(e.target.value)}
              >
                <option value="relevance">Best Match</option>
                <option value="newest">Newest</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
              </select>
            </div>
          `}
          <div class="plp-toolbar__view">
            <button
              class="plp-toolbar__view-btn${viewMode === 'grid' ? ' plp-toolbar__view-btn--active' : ''}"
              onClick=${() => onToggleView('grid')}
              aria-label="Grid view"
              aria-pressed=${viewMode === 'grid'}
            >
              <${GridIcon} />
            </button>
            <button
              class="plp-toolbar__view-btn${viewMode === 'list' ? ' plp-toolbar__view-btn--active' : ''}"
              onClick=${() => onToggleView('list')}
              aria-label="List view"
              aria-pressed=${viewMode === 'list'}
            >
              <${ListIcon} />
            </button>
          </div>
        </div>
      </div>
    `;
  }

  window.PlpToolbar = Toolbar;
})();
